import { useEffect, useState } from "react";
import type { FC } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";

import { Lightbox } from "components/Lightbox";
import Video from "components/Video/Video";

import { setIsSoundMuted } from "store/slices/user";
import { useAppDispatch } from "store/hooks";

import Capybara from "images/icons/oops.svg";

interface Props {
  videoIds: string[];
  attempt: number;
  onModalClose?: () => void;
}

export const SecondIncorrectAnswerModal: FC<Props> = ({ videoIds, attempt, onModalClose }) => {
  const { t } = useTranslation("tutoring");
  const [isShown, setIsShown] = useState(true);
  const [isWatched, setIsWatched] = useState(false);
  const dispatch = useAppDispatch();

  const onClose = () => {
    setIsShown(false);
    onModalClose?.();
  };

  const onWatch = () => {
    setIsWatched(true);
  };

  useEffect(() => {
    dispatch(setIsSoundMuted(isShown));
  }, [isShown]);

  const translationKey = attempt === 2 ? "second_incorrect" : "another_incorrect";

  // AIDEV-NOTE: Dřív bez `Modal.Header` — `onHide` už dřív šlo na `Modal` (ESC/backdrop
  // zavíral bez ohledu na `isWatched`), křížek tak jen zpřístupňuje existující cestu ven,
  // vynucení zhlédnutí videa přes disabled tlačítko v patičce se nemění. [TD-344]
  return (
    <Lightbox
      show={isShown}
      onHide={onClose}
      size="lg"
      bodyClassName="text-center"
      bodyTitleId="second-incorrect-answer-title"
      footer={
        <Button variant="azure" onClick={onClose} className="flex-grow-1" disabled={!isWatched}>
          <div className="px-5 text-nowrap">{t("modal.second_incorrect.close")}</div>
        </Button>
      }
    >
      <Capybara />
      {/* Velikost drží `.fs-16`, ať se sjednocením na `h2` (globálně 17 px) nezmění
          vzhled — původní `h4` měl 16 px ($h4-font-size). [TD-344] */}
      <h2 id="second-incorrect-answer-title" className={"my-20px fs-16"}>
        {t(`modal.${translationKey}.title`)}
      </h2>
      <p className="m-0">{t(`modal.${translationKey}.description`)}</p>

      <div className="py-20px">
        {videoIds.map(videoId => (
          <div key={videoId} className="mb-3">
            <Video videoId={videoId} />
          </div>
        ))}
      </div>

      <Form>
        <Form.Check
          id="video-modal-checkbox"
          name="video-modal-checkbox"
          type="checkbox"
          label={t(`modal.${translationKey}.label`)}
          checked={isWatched}
          onChange={onWatch}
          className="form-check--checkbox"
        />
      </Form>
    </Lightbox>
  );
};
